import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

export function GuestInviteDialog({ open, onOpenChange, onInvited }) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [cc, setCc] = useState("");
  const [redirect, setRedirect] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(""); setDisplayName(""); setCc(""); setRedirect("");
    api.get("/config").then((r) => setMessage((r.data.templates || {}).guestInvite || "")).catch(() => setMessage(""));
  }, [open]);

  const submit = async () => {
    if (!email.includes("@")) { toast.error(t("guest.needEmail")); return; }
    setBusy(true);
    try {
      const msg = message.replaceAll("{displayName_guest}", displayName || email);
      await api.post("/guests/invite", { email, displayName, message: msg, cc: cc || null, inviteRedirectUrl: redirect || null });
      toast.success(t("guest.sent"));
      onOpenChange(false);
      onInvited && onInvited();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "b2bPolicyBlocked" ? t("guest.errB2BBlocked") : apiErr(e), { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="guest-invite-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{t("guest.title")}</DialogTitle>
          <DialogDescription className="text-xs">{t("guest.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("guest.email")} *</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="guest@partner.com" data-testid="guest-email" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("guest.displayName")}</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="guest-displayname" className="rounded-sm border-zinc-300" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("guest.message")}</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="guest-message" className="rounded-sm border-zinc-300 text-sm" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("guest.cc")}</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="helpdesk@company.com" data-testid="guest-cc" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("guest.redirect")}</Label>
              <Input value={redirect} onChange={(e) => setRedirect(e.target.value)} placeholder="https://myapplications.microsoft.com" data-testid="guest-redirect" className="rounded-sm border-zinc-300 font-mono text-xs" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy} data-testid="guest-submit" className="rounded-sm bg-primary hover:bg-[#002480]">
            {busy && <Loader2 size={15} className="mr-2 animate-spin" />}{t("guest.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
